import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { api, apiError, fmtHours, fmtTime, MONTHS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const now = new Date();

export default function AdminOverview() {
  const { pharmacy } = useAuth();
  const types = pharmacy?.absence_types || [];
  const [month, setMonth] = useState(now.getMonth() + 1);
  const year = now.getFullYear();
  const [rows, setRows] = useState([]);
  const [today, setToday] = useState([]);
  const [pending, setPending] = useState(0);

  const load = useCallback(async () => {
    try {
      const [s, t, a] = await Promise.all([
        api.get("/attendance/summary", { params: { year, month } }),
        api.get("/attendance/today"),
        api.get("/absences", { params: { scope: "all" } }),
      ]);
      setRows(s.data.rows);
      setToday(t.data.records);
      setPending(a.data.filter((x) => x.status === "pending").length);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    const map = {
      csv: { url: "/export/csv", ext: "csv", label: "CSV" },
      xlsx: { url: "/export/xlsx", ext: "xlsx", label: "Excel" },
      pdf: { url: "/export/pdf", ext: "pdf", label: "PDF" },
    };
    const cfg = map[format];
    try {
      const res = await api.get(cfg.url, { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cartellini_${year}_${String(month).padStart(2, "0")}.${cfg.ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Cartellino ${cfg.label} scaricato`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const inShift = today.filter((r) => !r.clock_out);
  const totalMinutes = rows.reduce((s, r) => s + r.minutes, 0);

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm text-gray-600">Pannello titolare · codice farmacia <span className="font-mono-num">{pharmacy?.code}</span></p>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Panoramica</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="admin-month-select"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          >
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m} {year}</option>)}
          </select>
          <button
            data-testid="export-xlsx-btn"
            onClick={() => download("xlsx")}
            className="h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95"
          >
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={() => download("pdf")}
            className="h-11 px-4 rounded-lg bg-emerald-700 text-white text-sm font-semibold flex items-center gap-2 transition-colors hover:bg-emerald-800 active:scale-95"
          >
            <FileText className="h-4 w-4" /> PDF
          </button>
          <button
            data-testid="export-csv-btn"
            onClick={() => download("csv")}
            className="h-11 px-4 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold flex items-center gap-2 transition-colors hover:bg-emerald-50 active:scale-95"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {[
          { label: "In turno ora", value: inShift.length, id: "kpi-in-shift" },
          { label: "Ore team nel mese", value: fmtHours(totalMinutes), id: "kpi-total-hours" },
          { label: "Richieste in attesa", value: pending, id: "kpi-pending" },
        ].map((k) => (
          <div key={k.id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <p className="text-xs uppercase tracking-wider text-gray-600">{k.label}</p>
            <p data-testid={k.id} className="font-mono-num text-3xl font-bold mt-2">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="team-report">
        <h2 className="text-base font-semibold">Report mensile team</h2>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-600 mt-4">Nessun operatore registrato.</p>
        ) : (
          <div className="mt-4 -mx-2 sm:mx-0 overflow-x-auto max-h-[520px] overflow-y-auto rounded-lg">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                  <th className="pb-3 pr-3">Operatore</th><th className="pb-3 pr-3">Effettive</th><th className="pb-3 pr-3">Previste</th><th className="pb-3 pr-3">Delta</th><th className="pb-3 pr-3">Giorni</th>
                  {types.map((t) => <th key={t.key} className="pb-3 pr-3">{t.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const delta = r.delta_minutes ?? (r.minutes - (r.planned_minutes || 0));
                  const deltaColor = delta === 0 ? "text-gray-500" : delta > 0 ? "text-emerald-700" : "text-red-600";
                  const sign = delta > 0 ? "+" : delta < 0 ? "-" : "";
                  return (
                    <tr key={r.operator_id} data-testid={`team-row-${r.operator_id}`} className="border-t border-gray-100">
                      <td className="py-3 pr-3 font-medium whitespace-nowrap">{r.operator_name}</td>
                      <td className="py-3 pr-3 font-mono-num">{fmtHours(r.minutes)}</td>
                      <td className="py-3 pr-3 font-mono-num text-gray-600">{fmtHours(r.planned_minutes || 0)}</td>
                      <td className={`py-3 pr-3 font-mono-num font-semibold ${deltaColor}`} data-testid={`delta-${r.operator_id}`}>
                        {sign}{fmtHours(Math.abs(delta))}{delta < 0 ? " ⚠" : ""}
                      </td>
                      <td className="py-3 pr-3 font-mono-num">{r.days}</td>
                      {types.map((t) => (
                        <td key={t.key} className="py-3 pr-3 font-mono-num">{r.absences?.[t.key] || 0}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="today-board">
        <h2 className="text-base font-semibold">Timbrature di oggi</h2>
        {today.length === 0 ? (
          <p className="text-sm text-gray-600 mt-4">Nessuna timbratura oggi.</p>
        ) : (
          <div className="mt-4 -mx-2 sm:mx-0 overflow-x-auto max-h-[400px] overflow-y-auto rounded-lg">
            <table className="w-full text-sm min-w-[540px]">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                  <th className="pb-3 pr-3">Operatore</th><th className="pb-3 pr-3">Ingresso</th><th className="pb-3 pr-3">Uscita</th><th className="pb-3 pr-3 text-right">Ore</th>
                </tr>
              </thead>
              <tbody>
                {today.map((r) => (
                  <tr key={r.id} data-testid={`today-row-${r.id}`} className="border-t border-gray-100">
                    <td className="py-3 pr-3 font-medium whitespace-nowrap">{r.operator_name}</td>
                    <td className="py-3 pr-3 font-mono-num">{fmtTime(r.clock_in)}</td>
                    <td className="py-3 pr-3 font-mono-num">{r.clock_out ? fmtTime(r.clock_out) : "in corso"}</td>
                    <td className="py-3 pr-3 font-mono-num text-right">{r.minutes != null ? fmtHours(r.minutes) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
